import * as React from 'react';

function setRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  if (ref != null) {
    ref.current = value;
  }
}

/**
 * React 19 treats a returned function from a callback ref as a cleanup.
 * Stock composeRefs returns that cleanup, then React detaches/reattaches every
 * commit. Presence's ref calls setNode, which re-renders, which repeats until
 * minified error #185 (maximum update depth) and a white screen.
 *
 * Never return a cleanup here. Use the classic callback-ref protocol: React
 * calls the same function with `null` on detach.
 */
function composeRefs(...refs) {
  return (node) => {
    for (let i = 0; i < refs.length; i += 1) {
      setRef(refs[i], node);
    }
  };
}

/**
 * Keep the callback identity stable even when callers pass inline refs.
 * Latest refs are read at attach/detach time.
 *
 * @see https://github.com/radix-ui/primitives/issues/3664
 */
function useComposedRefs(...refs) {
  const refsRef = React.useRef(refs);
  refsRef.current = refs;
  return React.useCallback((node) => {
    const currentRefs = refsRef.current;
    for (let i = 0; i < currentRefs.length; i += 1) {
      setRef(currentRefs[i], node);
    }
  }, []);
}

export { composeRefs, useComposedRefs };
